import { useCallback, useState, useMemo, useEffect } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { useDate } from "@/hooks/use-date/useDate";
import { Icon } from "@/components/icons/Icon";
import type { InspectionData } from "../../inspection.d";
import ProjectModal from "../ProjectModal";

interface InspectionGeneralDataProps {
  inspectionData: InspectionData;
}

const InspectionGeneralData = ({
  inspectionData,
}: InspectionGeneralDataProps) => {
  const translate = useTranslate();
  const { formatDate, getDaysSince } = useDate();

  const [showMore, setShowMore] = useState(false);
  const [projectForModal, setProjectForModal] = useState("");
  const [project, setProject] = useState<string | null>(null);

  useEffect(() => {
    if (inspectionData.project) setProject(inspectionData.project);
  }, []);

  const changeShowMore = useCallback(() => {
    const newState = !showMore;
    setShowMore(newState);
  }, [showMore]);

  const dataBoxes = useMemo(() => {
    if (!inspectionData) return [];

    type KeyValue = { property: string; value: any; key: string };

    const keys: (keyof typeof inspectionData)[] = [
      "project",
      "esnIn",
      "esnOut",
      "nsCradle",
      "owner",
      "colour",
      "isCsPmo",
    ];

    const addValue = (key: string, data: any) => {
      if (key === "isCsPmo") return data ? translate("yes") : "No";
      if (!data) return "-";
      return data;
    };

    const dataObjects: KeyValue[] = keys.map((key) => ({
      property: key !== "isCsPmo" ? translate(key) : translate("indicateCsPmo"),
      value: addValue(key, inspectionData[key]),
      key: key,
    }));

    return dataObjects;
  }, [inspectionData, translate]);

  const openCloseProjectModal = useCallback(() => {
    if (projectForModal === "") {
      if (project) {
        setProjectForModal(project);
      } else {
        setProjectForModal("-");
      }
    } else {
      setProjectForModal("");
    }
  }, [projectForModal, project]);

  const getNewProject = useCallback((project: string) => {
    setProject(project);
    setProjectForModal("");
  }, []);

  return (
    <div className="w-full flex flex-col gap-y-2">
      <div className="w-full flex flex-row justify-between pb-2">
        <div className="flex flex-row justify-start gap-x-4 h-[34px] items-center">
          <div className="font-ib-bold text-[26px] leading-[34px] text-ib-gray-darkest">
            {`${translate("stand")} ${inspectionData?.nsStand}`}
          </div>
          <div className="h-7 flex flex-row gap-x-2 bg-ib-gray-lightest rounded-lg items-center px-4">
            <Icon name="engine" className="size-4 text-ib-black" />
            <div className="text-ib-main">
              <span className="text-ib-gray-main mr-0.5">
                {translate("engine")}:
              </span>
              <span className="text-ib-gray-darkest">
                {inspectionData?.engineName}
              </span>
            </div>
          </div>
          {!inspectionData?.endDate ? (
            <div className="h-7 flex flex-row gap-x-2 bg-ib-gray-lightest rounded-lg items-center px-4 min-w-[209px] justify-between">
              <div className="text-ib-main">
                <span className="text-ib-gray-main mr-0.5">
                  {translate("start")}:
                </span>
                <span className="text-ib-gray-darkest">
                  {formatDate(inspectionData?.createdAt)}
                </span>
              </div>
              <div className="text-ib-main text-ib-gray-darkest font-ib-bold">
                {getDaysSince(inspectionData?.createdAt)}
              </div>
            </div>
          ) : null}
        </div>
        <button
          onClick={changeShowMore}
          className="bg-ib-whithe flex flex-row p-x-0 h-4 gap-x-2 text-ib-main items-center font-[IberiaText] text-ib-red-main hover:text-ib-red-dark self-end"
        >
          {translate(showMore ? "seeLessDetails" : "seeMoreDetails")}
          <Icon
            name={showMore ? "chevronUp" : "chevronDown"}
            className="size-4"
          />
        </button>
      </div>
      {showMore ? (
        <div className="w-full pt-4 border-t border-t-ib-gray-lightest flex flex-row flex-wrap gap-x-4">
          {dataBoxes.map((box) => (
            <div
              key={box.key}
              className={`flex flex-col gap-y-0.5 py-4 ${
                box.key === "project" ? "pr-4" : "px-4"
              }`}
            >
              <div className="text-ib-main text-ib-gray-main">
                {box.property}
              </div>
              <div className="flex flex-row gap-x-2 items-center text-ib-main text-ib-gray-darkest font-ib-bold">
                {box.key === "project" && !inspectionData.endDate ? (
                  <>
                    {project ? project : null}
                    <button
                      className="text-ib-red-main flex flex-row gap-x-2 font-ib-normal"
                      onClick={openCloseProjectModal}
                    >
                      {!project ? translate("add") : null}
                      <Icon
                        name={!project ? "circlePlus" : "pencil"}
                        className="size-[18px]"
                      />
                    </button>
                  </>
                ) : (
                  box.value
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {projectForModal ? (
        <ProjectModal
          inspectionId={inspectionData.id}
          project={projectForModal}
          closeModal={openCloseProjectModal}
          getNewProject={getNewProject}
        />
      ) : null}
    </div>
  );
};

export default InspectionGeneralData;
